<template>
  <EnvBadge :user-email="euStore.formData.email" />
  <PageLoader />
  <SessionExpired v-if="authStore.hasAuthError && !isActionLinkPage" />
  <router-view v-else />
</template>

<script setup>
import SessionExpired from '@shared/components/SessionExpired.vue'

import EnvBadge from '@shared/components/EnvBadge.vue'
import PageLoader from '@shared/components/Pageloader.vue'
import { changeLocale } from '@shared/localization'
import {
  isActionLinkRouteName,
  routeNameOrStartupPage,
} from '@shared/actionLinks/pages.js'
import { computed, watch } from 'vue'
import { getLangCodeByLanguage, setPageTitle } from '@shared/utils'
import { useI18n } from 'petite-vue-i18n'
import { useRoute } from 'vue-router'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useEuStore from '@eu/app/stores/useEuStore.js'
import useAuthStore from '@shared/stores/useAuthStore.js'
import * as apiRequest from '@shared/api-endpoints'

const { t } = useI18n()
const userStore = useUserStore()
const corporateStore = useCorporateStore()
const euStore = useEuStore()
const authStore = useAuthStore()
const route = useRoute()

const lang = computed(() => userStore.lang)
// An action link page is public and works with its link alias, never with a session stored in the tab.
const isActionLinkPage = computed(() =>
  isActionLinkRouteName(routeNameOrStartupPage(route.name)),
)
let languageSyncAbortController = null

watch(route, () => {
  if (!euStore.startRoute) {
    euStore.startRoute = route.name
  }
  document.documentElement.scrollTop = 0
})

// update the i18n language whenever the language in the store changes
watch(
  [
    lang,
    () => authStore.isAuthenticated,
    () => userStore.user?.profile?.language,
  ],
  async ([lang, isAuthenticated, profileLanguage]) => {
    const hasLoadedProfile = isAuthenticated && !!userStore.user?.profile

    changeLocale(getLangCodeByLanguage(lang), document.documentElement)

    // Requests made with an expired stored session would get a 401, which replaces the page.
    if (isActionLinkPage.value) return

    // Unauthenticated users still need a direct corporate reload when the UI language changes.
    if (!hasLoadedProfile && corporateStore.corporate) {
      try {
        await apiRequest.loadCorporate({
          key: corporateStore.corporate.key,
        })
      } catch (error) {
        void error
      }
    }

    // Authenticated users sync language through the profile update, which also refreshes corporate data.
    if (!hasLoadedProfile || lang === profileLanguage) {
      return
    }

    try {
      languageSyncAbortController?.abort(
        'A newer language sync request was triggered',
      )
      languageSyncAbortController = new AbortController()
      await apiRequest.setUserProfile({
        language: lang,
        abortSignal: languageSyncAbortController.signal,
      })
    } catch (error) {
      if (error?.code !== 'ERR_CANCELED') {
        void error
      }
    }
  },
  { immediate: true },
)

setPageTitle(t('b2b__landing_page__generic__page_title'))
</script>
